import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from './AuthContext'

const CoordinatorModeContext = createContext({
  isCoordinator: false,
  coordinatorMode: false,
  toggleCoordinatorMode: () => {},
})

export const CoordinatorModeProvider = ({ children }) => {
  const { user } = useAuth()
  const [isCoordinator, setIsCoordinator] = useState(false)
  const [coordinatorMode, setCoordinatorMode] = useState(false)
  // True until the first load resolves. Consumers (e.g. TreasurerReport) must
  // wait on this before gating — otherwise a not-yet-loaded coordinatorMode=false
  // false-trips coordinator-only pages. Starts true so gates don't fire on the
  // very first render.
  const [loading, setLoading] = useState(true)

  // On load: fetch is_coordinator AND coordinator_mode_active from DB.
  // coordinator_mode_active is the source of truth for RLS — not localStorage.
  useEffect(() => {
    if (!user) { setIsCoordinator(false); setCoordinatorMode(false); setLoading(false); return }

    let cancelled = false
    setLoading(true)
    ;(async () => {
      // Retry on transient network error. A flaky mobile load previously gave up
      // after one failed fetch and left coordinatorMode=false until a full app
      // reload — which false-gated the treasurer report ("must be in coordinator
      // mode") even for a coordinator who WAS in coordinator mode.
      for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_coordinator, coordinator_mode_active')
          .eq('id', user.id)
          .maybeSingle()
        if (cancelled) return
        if (!error) {
          const isCoord = data?.is_coordinator === true
          setIsCoordinator(isCoord)
          if (!isCoord) {
            // Not a coordinator — ensure DB is set to false. Await so a
            // failure surfaces in logs instead of silently leaving the DB
            // in a stale "true" state that downstream RLS would trust.
            setCoordinatorMode(false)
            const { error: resetErr } = await supabase
              .from('profiles')
              .update({ coordinator_mode_active: false })
              .eq('id', user.id)
            if (resetErr) console.warn('CoordinatorMode reset-on-load failed:', resetErr.message)
          } else {
            // Restore last known state from DB.
            setCoordinatorMode(data?.coordinator_mode_active === true)
          }
          if (!cancelled) setLoading(false)
          return
        }
        console.warn(`CoordinatorMode check error (attempt ${attempt + 1}/3):`, error.message)
        await new Promise(r => setTimeout(r, 500 * (attempt + 1)))
      }
      // Retries exhausted (or torn down). Resolve loading so consumers don't hang;
      // leave isCoordinator/coordinatorMode at their last-known values rather than
      // forcing false (which would be the false-gate bug all over again).
      if (!cancelled) setLoading(false)
    })()
    return () => { cancelled = true }
  }, [user?.id, user])

  const toggleCoordinatorMode = useCallback(async () => {
    if (!user) return
    const next = !coordinatorMode
    setCoordinatorMode(next)
    // TODO: enforce via RLS that only is_coordinator=true users can toggle this.
    // Write to DB so RLS policies can see the current mode.
    const { error } = await supabase
      .from('profiles')
      .update({ coordinator_mode_active: next })
      .eq('id', user.id)
    if (error) {
      console.error('toggleCoordinatorMode error:', error)
      setCoordinatorMode(!next)
    }
  }, [coordinatorMode, user])

  const value = useMemo(
    () => ({ isCoordinator, coordinatorMode, coordinatorModeLoading: loading, toggleCoordinatorMode }),
    [isCoordinator, coordinatorMode, loading, toggleCoordinatorMode]
  )

  return (
    <CoordinatorModeContext.Provider value={value}>
      {children}
    </CoordinatorModeContext.Provider>
  )
}

export const useCoordinatorMode = () => useContext(CoordinatorModeContext)
